import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { v4 } from 'uuid';

import { NavLink } from './HeaderStyles';
import useOnClickOutside from './OnClickOutside';
import Icon from '../Icon';
import { theme } from '../../styles/GlobalStyles';
import { isHelpCenterSection, isBlogSection, isAuthorSection } from '../../utils/pageURLCheck';

export const NavWrapper = styled.nav`
  position: absolute;
  display: block;
  z-index: 3;
  top: -142vmax;
  right: -142vmax;
  width: ${props => (props.isOpen ? '284vmax' : '1px')};
  height: ${props => (props.isOpen ? '284vmax' : '1px')};
  background-color: ${theme.navbar.dark};
  border-radius:50%;
  color: ${theme.body.dark};
  padding: 2.625rem 3rem;
  outline: none;

  transform: ${props => (props.isOpen ? 'scale(1)' : 'scale(0)')};
  transform-origin: 50% 50%;
  transition: transform .5s cubic-bezier(0.755, 0.050, 0.855, 0.060);
  will-change: transform;

  ul {
    position: relative;
    top: 150vmax;
    width: 170vw;
    flex-direction: column;
  }

  li {
    display: flex;
    margin: 0.468rem 0rem;
    opacity: ${props => (props.isOpen ? '1' : '0')};
    transition: opacity .5s cubic-bezier(0.000, 0.995, 0.990, 1.000);
  }

  .drop-menu {
    position: relative;
    margin: 0;

    :hover {
      cursor: pointer;
    }
  }

  .drop-header {
    display: none;
  }
  
  @media (min-width: 990px) {
    width: 100vw;
    height: unset;
    max-width: 30rem;
    position: unset;
    background-color: transparent;
    color: inherit;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: ${props => (props.scrolled ? 'center' : 'flex-start')};
    transition: opacity 0.1s linear;
    opacity: ${props => (props.searchOpen ? '0' : '1')};
    flex-grow: 1;
    z-index: ${props => (props.scrolled ? '-1' : '2')};
    transform: scale(1);

    ul {
      position: relative;
      width: auto;
      display: flex;
      transition: opacity 0.1s linear;
      opacity: ${props => (props.searchOpen ? '0' : '1')};
      flex-direction: row;
      top: 0;
    }

    li {
      position: relative;
      margin: 0 0.4rem;
      transition: opacity 0.1s linear;
      opacity: ${props => (props.searchOpen ? '0' : '1')};
    }
    .drop-header {
      display: block;
    }
  }
  @media (min-width: 1200px) {
    max-width: 45rem;
  }
`;

const DropDown = styled.div`
  position: absolute;
  display: ${props => (props.isHover ? 'block' : 'none')};
  top: 35px;
  left: 0;
  width: ${props => props.width};
  height: 104px;
  padding: 12px 15px;
  background: ${theme.navbar.light};
  box-shadow: 0px 1px 8px rgba(0, 0, 0, 0.078);
  border-radius: 3px;
  & a {
    display: block;
    margin-top: 14px;
    padding: 0;
    color: ${theme.body.light};
  }
  & a:nth-child(1) {
    margin-top: 0;
  }
  @media(max-width: 989px) {
    position: relative;
    top: 0;
    height: auto;
    width: 100%;
    display: block;
    background: #1F2432;
    padding: 0;
    margin: 0;
    box-shadow: none;
    border-radius: 0;

    & a:nth-child(1) {
      margin: 0.468rem 0rem;
    }
    & a {
      color: white;
      margin: 0.468rem 0rem;
      padding: 0.66rem 0.5rem 0.66rem 0;
    }
  }
`;

const DropIcon = styled.div`
  width: 7px;
  margin-left: 7px;
  display: inline-block;
  svg {
    vertical-align: middle;
    transition: transform 0s linear;
    transform: ${props => props.transform};  
  }
`;

const Nav = ({
  children, isOpen, scrolled, menu, pathname, body, helpNavScroll, searchOpen
}) => {
  const ref = useRef();
  const [isHover, setHover] = useState(false);
  useOnClickOutside(ref, () => setHover(false));

  if (typeof window !== 'undefined') {
    if (searchOpen) {
      document.getElementById('headerSearchInput').focus();
    }
    if (!isHelpCenterSection(pathname) && !isBlogSection(pathname) && !isAuthorSection(pathname)) {
      return (
        <>
          <NavWrapper isOpen={isOpen} scrolled={scrolled}>
            {(scrolled && body) || (
              <ul key={v4()}>
                {menu.links.map((element, i) => {
                  if (i < 4) {
                    return (
                      <li key={v4()}>
                        <NavLink to={element.url} activeClassName="active">{element.name}</NavLink>
                      </li>
                    );
                  }
                  return true;
                })}
                <li
                  className="drop-menu"
                  onClick={() => setHover(!isHover)}
                  aria-hidden="true"
                  ref={ref}
                >
                  <NavLink className="drop-header">
                    Resources
                    <DropIcon alt="drop-icon" transform={isHover ? 'rotate(180deg)' : ''}>
                      <Icon name="dropArrow" />
                    </DropIcon>
                  </NavLink>
                  <DropDown isHover={isHover} width="110px">
                    {menu.links.map((element, i) => {
                      if (i > 3) {
                        return (
                          <NavLink
                            onClick={() => setHover(false)}
                            to={element.url}
                            key={v4()}
                          >
                            {element.name}
                          </NavLink>
                        );
                      }
                      return true;
                    })}
                  </DropDown>
                </li>
              </ul>
            )}
            {children}
          </NavWrapper>
        </>
      );
    }
    if (isHelpCenterSection(pathname)) {
      return (
        <>
          <NavWrapper isOpen={isOpen} scrolled={scrolled} searchOpen={searchOpen}>
            {(scrolled && body) || (
              <ul key={v4()}>
                {menu.links.map((element, i) => {
                  if (i < 3) {
                    return (
                      <li key={v4()}>
                        <NavLink to={element.url} helpNavScroll={helpNavScroll} activeClassName="active">{element.name}</NavLink>
                      </li>
                    );
                  }
                  return true;
                })}
                <li
                  className="drop-menu"
                  onClick={() => setHover(!isHover)}
                  aria-hidden="true"
                  ref={ref}
                >
                  <NavLink className="drop-header" helpNavScroll={helpNavScroll}>
                    More
                    <DropIcon alt="drop-icon" transform={isHover ? 'rotate(180deg)' : ''}>
                      <Icon name="dropArrow" color={helpNavScroll > 0 && helpNavScroll < 400 ? `${theme.body.dark}` : `${theme.body.light}`} />
                    </DropIcon>
                  </NavLink>
                  <DropDown isHover={isHover} width="114px">
                    {menu.links.map((element, i) => {
                      if (i > 2) {
                        return (
                          <NavLink
                            onClick={() => setHover(false)}
                            to={element.url}
                            activeClassName="active"
                            key={v4()}
                          >
                            {element.name}
                          </NavLink>
                        );
                      }
                      return true;
                    })}
                  </DropDown>
                </li>
              </ul>
            )}
            {children}
          </NavWrapper>
        </>
      );
    }
    return (
      <>
        <NavWrapper isOpen={isOpen} scrolled={scrolled}>
          {(scrolled && body) || (
            <ul key={v4()}>
              {menu.links.map(element => (
                <li key={v4()}>
                  <NavLink to={element.url} activeClassName="active">{element.name}</NavLink>
                </li>
              ))}
            </ul>
          )}
          {children}
        </NavWrapper>
      </>
    );
  }
  return true;
};

Nav.defaultProps = {
  isOpen: false,
  scrolled: false,
  searchOpen: false,
  children: '',
  body: '',
  menu: {
    name: '',
    links: [
      {
        url: '/',
        name: 'Defaul Name',
      },
    ],
  },
};

Nav.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  children: PropTypes.any,
  isOpen: PropTypes.bool,
  scrolled: PropTypes.bool,
  searchOpen: PropTypes.bool,
  body: PropTypes.string,
  helpNavScroll: PropTypes.number,
  pathname: PropTypes.string.isRequired,
  menu: PropTypes.shape({
    name: PropTypes.string,
    links: PropTypes.arrayOf(
      PropTypes.shape({
        url: PropTypes.string,
        name: PropTypes.string,
      })
    ),
  }),
};

export default Nav;
